import { Message } from "@moe/priv/model/message";
import { chunk } from "@moe/priv/utils";
import { InfiniteData } from "@tanstack/react-query";
import { queryClient } from "@web/routes/__root";
import { historyPageSize } from "./History";

/**
 * Manages the react query chat history for a specific chat ID.
 */
export class HistoryCache {
  private readonly chatID: number;

  constructor({ chatID }: { chatID: number }) {
    this.chatID = chatID;
  }

  /**
   * Retrieves the message(s) with the specified ID(s) from the chat history.
   *
   * @param id - The ID or array of IDs of the message(s) to retrieve.
   * @returns The message(s) with the specified ID(s), or `undefined` if the message(s) are not found in the chat history.
   */
  getByID(id: number): Message | undefined;
  getByID(id: number[]): Message | undefined;
  getByID(id: number | number[]): (Message | undefined) | (Message | undefined)[];
  getByID(id: number | number[]): (Message | undefined) | (Message | undefined)[] {
    const chatHistory = this.getAll();
    if (Array.isArray(id)) return id.map((i) => this.getByID(i));
    return chatHistory.find((m) => (Array.isArray(m) ? m[0].id === id : m.id === id));
  }

  /**
   * Updates the chat history for the current chat ID.
   * @param value - The new chat history to update.
   */
  private updateHistory(value: Message[]) {
    queryClient.setQueryData<InfiniteData<Message[], unknown>>(["chatHistory", this.chatID], (data) => {
      if (!data) return data;
      return {
        pages: chunk(value, historyPageSize),
        pageParams: data.pageParams
      };
    });
  }

  /**
   * Append a new message to the current chat.
   * @param message - The message to add to the chat history.
   */
  push(message: Message) {
    const chatHistory = this.getAll();
    chatHistory.push(message);
    this.updateHistory(chatHistory);
  }

  /**
   * Retrieves the chat history for the current chat
   * @returns The deep cloned chat history
   */
  getAll(): Message[] {
    const chatHistoryPages = queryClient.getQueryData<InfiniteData<Message[], unknown>>(["chatHistory", this.chatID]);
    const chatHistory = chatHistoryPages?.pages.flat() || [];
    const cloned: typeof chatHistory = JSON.parse(JSON.stringify(chatHistory));
    return cloned;
  }

  length() {
    const chatHistoryPages = queryClient.getQueryData<InfiniteData<Message[], unknown>>(["chatHistory", this.chatID]);
    return chatHistoryPages?.pages.flat().length || 0;
  }

  /**
   * Retrieves the last message in the chat history for the curent chat
   */
  getLast(): Message | undefined {
    const chatHistoryPages = queryClient.getQueryData<InfiniteData<Message[], unknown>>(["chatHistory", this.chatID]);
    return chatHistoryPages?.pages.at(-1)?.at(-1);
  }

  /**
   * Deletes one or more messages from the chat history for the specified chat ID.
   *
   * @param id - The ID or array of IDs of the message(s) to delete from the chat history.
   */
  delete(id: number | number[]) {
    const chatHistory = this.getAll();
    const filtered = chatHistory.filter((m) => {
      let baseID: number;
      if (Array.isArray(m)) baseID = m[0].id;
      else baseID = m.id;
      if (Array.isArray(id)) return !id.includes(baseID);
      return baseID !== id;
    });
    this.updateHistory(filtered);
  }

  /**
   * Replaces a message in the chat history for the specified chat ID.
   * @param id - The ID of the message to replace.
   * @param message - The new message to replace the existing one.
   */
  replace(id: number, message: Message) {
    const chatHistory = this.getAll();
    const index = chatHistory.findIndex((m) => (Array.isArray(m) ? m[0].id === id : m.id === id));
    if (index === -1) return;
    chatHistory[index] = message;
    this.updateHistory(chatHistory);
  }

  /**
   * Break glass in case of emergency.
   */
  invalidate() {
    return queryClient.invalidateQueries({
      queryKey: ["chatHistory", this.chatID]
    });
  }
}
